import { css } from '@emotion/react';
import { useLoadableFeature } from '@hackler/react-sdk';
import { Box, Divider, Flex, Group, desktopFirstMediaQuery, useTheme } from '@inflearn/ds-react';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { Sidebar } from '../../FSD/shared/ui/sidebar/Sidebar';
import useUserId from '../../hooks/urlQueryString/useUserId';

import useHandleDormantUser from '../../hooks/useHandleDormantUser';
import { NEW_LAYOUT } from '../../lib/hackle/featureKeys';
import InstructorProfileNavigation from './UserProfileNavigation/index.instructor';
import StudentProfileNavigation from './UserProfileNavigation/index.student';

type Props = {
  children: ReactNode;
  isInstructor: boolean;
  isInactive: boolean;
  asideChildren?: ReactNode;
  hasUserInfo?: boolean;
};

export default function Layout({
  children,
  isInstructor,
  isInactive,
  asideChildren,
  hasUserInfo = true,
}: Props) {
  const { isOn, isLoading } = useLoadableFeature(NEW_LAYOUT);

  if (isLoading) {
    return null;
  }

  if (!isOn) {
    return (
      <OldLayout
        isInstructor={isInstructor}
        isInactive={isInactive}
        asideChildren={asideChildren}
        hasUserInfo
      >
        {children}
      </OldLayout>
    );
  }

  return (
    <NewLayout isInstructor={isInstructor} isInactive={isInactive} hasUserInfo={hasUserInfo}>
      {children}
    </NewLayout>
  );
}

function OldLayout({ children, isInstructor, isInactive, asideChildren }: Props) {
  const theme = useTheme();

  useHandleDormantUser(isInactive);

  return (
    <Group maw={1200} m="0 auto" align="normal" css={stylesContainer}>
      <Box
        component="aside"
        pt={40}
        pr={32}
        css={[{ borderRight: `1px solid ${theme.colors.gray[2]}` }, stylesNavigation]}
      >
        <Box w={243}>
          {isInstructor ? <InstructorProfileNavigation /> : <StudentProfileNavigation />}
          {asideChildren}
        </Box>
      </Box>
      <Divider
        size={8}
        color="gray.0"
        css={{
          display: 'none',
          [desktopFirstMediaQuery.tablet]: { display: 'block' },
        }}
      />
      <Box css={stylesContent}>{children}</Box>
    </Group>
  );
}

function NewLayout({ children, isInstructor, isInactive, hasUserInfo }: Props) {
  useHandleDormantUser(isInactive);
  const { targetUserId } = useUserId();
  const router = useRouter();
  const pathUserId = router.query.id;
  const isMe = Number(pathUserId) === targetUserId;

  return (
    <Flex
      maw={1200}
      m="0 auto"
      css={{
        justifyContent: isMe ? 'space-between' : 'center',
        padding: '32px',
        [desktopFirstMediaQuery.tablet]: {
          padding: '20px 16px',
        },
      }}
    >
      {isMe && (
        <Sidebar
          styles={{
            root: {
              minWidth: 190,
              [desktopFirstMediaQuery.tablet]: {
                display: 'none',
              },
            },
          }}
        />
      )}
      <Box
        css={{
          paddingLeft: isMe ? 56 : 0,
          maxWidth: isMe ? '100%' : '890px',
          flex: 1,
          minWidth: 0,

          [desktopFirstMediaQuery.tablet]: {
            paddingLeft: 0,
          },
        }}
      >
        {hasUserInfo && (
          <>
            {isInstructor ? <InstructorProfileNavigation /> : <StudentProfileNavigation />}
            <Divider size={8} color="gray.0" />
          </>
        )}
        <Box>{children}</Box>
      </Box>
    </Flex>
  );
}

const stylesContainer = css({
  minHeight: 800,
  padding: '0 32px',

  [desktopFirstMediaQuery.tablet]: {
    padding: 0,
  },
  [desktopFirstMediaQuery.mobile]: {
    padding: 0,
  },
});

const stylesNavigation = css({
  [desktopFirstMediaQuery.tablet]: {
    width: '100%',
    border: 'none',
    padding: '24px 32px 0',
    '& > div': {
      width: '100%',
    },
  },
  [desktopFirstMediaQuery.mobile]: {
    padding: '24px 16px 0',
  },
});

const stylesContent = css({
  flex: 1,
  width: '100%',
  height: '100%',
  minWidth: 0,
  padding: '20px 0 24px 32px',
  [desktopFirstMediaQuery.tablet]: {
    padding: 32,
  },
  [desktopFirstMediaQuery.mobile]: {
    padding: '0 16px 20px',
  },
});
